// Pure helpers for the first-time care-team onboarding review modal.
// Durable "past the onboarding tutorial" flag = care_plan.context.onboarding_review.confirmed_at
// (written by the confirm flow via pathway_apply_requested → WF36 → WF15 — a dedicated
// sibling key so the rich access_subtrack bundle + claims_* survive WF15's shallow
// context merge). The onb_subtrack_confirm task is a secondary signal only (clears the
// clinician dashboard).

type Json = Record<string, unknown>;

export interface AccessSubtrack {
  track?: string | null;
  subtrack_id?: string | null;
  mnemonic?: string | null;
  status?: 'suggested' | 'confirmed' | 'unresolved' | string;
  [k: string]: unknown;
}

export interface OnboardingReview {
  confirmed_at?: string | null;
  confirmed_by?: string | null;
  subtrack_id?: string | null;
  [k: string]: unknown;
}

export const SUBTRACK_CONFIRM_TEMPLATE = 'onb_subtrack_confirm';
const CLOSED_TASK_STATUSES = new Set(['completed', 'cancelled', 'missed']);
const PRE_ACTIVE_PERIOD_STATUSES = new Set(['pending', 'pending_eligibility', 'pending_alignment']);

function templateCodeOf(task: Json): string {
  const t = task as {
    template?: { template_code?: string };
    template_code?: string;
    context_json?: { template_code?: string };
  };
  return t.template?.template_code || t.template_code || t.context_json?.template_code || '';
}

// context arrives decoded from the Tier-1 /care-plans GET, but be defensive (it can
// be a JSON string on some transports).
function readContext(carePlan: Json | null | undefined): Json | null {
  let ctx: unknown = carePlan?.context ?? null;
  if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch { ctx = null; } }
  return ctx && typeof ctx === 'object' ? (ctx as Json) : null;
}

// The auto-resolved subtrack the modal displays (read-only suggestion). Not the gate.
export function readAccessSubtrack(carePlan: Json | null | undefined): AccessSubtrack | null {
  const sub = readContext(carePlan)?.access_subtrack;
  return sub && typeof sub === 'object' ? (sub as AccessSubtrack) : null;
}

// The durable onboarding-review flag (the gate's source of truth).
export function readOnboardingReview(carePlan: Json | null | undefined): OnboardingReview | null {
  const r = readContext(carePlan)?.onboarding_review;
  return r && typeof r === 'object' ? (r as OnboardingReview) : null;
}

export function isOnboardingReviewed(carePlan: Json | null | undefined): boolean {
  return !!readOnboardingReview(carePlan)?.confirmed_at;
}

// CMS-provisioned SYNTHETIC beneficiaries for the ACCESS IMPL sandbox
// (docs/CMS/testing/test-data.csv). Not real people, so no real patient can match —
// which is why the test-patient affordances below are safe to ship unconditionally.
// They have no Elation chart by design: WF16 is skipped / fails for them.
export const CMS_TEST_MBIS = new Set([
  '1CA1TE1AR30', '7MT6TE9HT50', '5J18TE6KV90', '6GH6TE8DK80', '3DX7TE3TQ10', '1EG4TE5MK72',
]);

// MBI lives on insurance.member_id (medicare_mbi is unpopulated today).
export function isCmsTestPatient(insurance: Json | null | undefined): boolean {
  const ins = insurance as { member_id?: unknown; medicare_mbi?: unknown } | null | undefined;
  const mbi = String(ins?.medicare_mbi || ins?.member_id || '').trim().toUpperCase();
  return CMS_TEST_MBIS.has(mbi);
}

// The open onb_subtrack_confirm task (if any) — feeds the stage guard and the
// secondary task-completion on finish.
export function findOpenSubtrackConfirm(tasks: Json[] | null | undefined): Json | null {
  if (!Array.isArray(tasks)) return null;
  return tasks.find((t) => (
    templateCodeOf(t) === SUBTRACK_CONFIRM_TEMPLATE
    && !CLOSED_TASK_STATUSES.has(String((t as { status?: string }).status || ''))
  )) ?? null;
}

// Gate: show the first-time review while the patient is still in the onboarding
// tutorial stage = not yet reviewed AND (pre-active care period OR an open
// onb_subtrack_confirm task). The stage guard stops the modal from retroactively
// nagging long-aligned legacy patients who were never reviewed.
export function needsOnboardingReview(
  { carePlan, carePeriod, tasks }: {
    carePlan: Json | null | undefined;
    carePeriod: Json | null | undefined;
    tasks: Json[] | null | undefined;
  },
): boolean {
  if (isOnboardingReviewed(carePlan)) return false;
  const periodStatus = String((carePeriod as { status?: string } | null)?.status || '');
  const preActive = PRE_ACTIVE_PERIOD_STATUSES.has(periodStatus);
  const hasOpenTask = findOpenSubtrackConfirm(tasks) != null;
  return preActive || hasOpenTask;
}

// Audit note for the secondary onb_subtrack_confirm completion. `at` is passed in
// (no Date.now() here) so the note is deterministic + testable.
export function buildReviewNote(
  {
    reviewerName, reviewerRole, elationVerified, consentVerified, subtrackId, resolvedSubtrackId, at,
  }: {
    reviewerName?: string | null;
    reviewerRole?: string | null;
    elationVerified: boolean;
    consentVerified: boolean;
    subtrackId?: string | null;
    resolvedSubtrackId?: string | null;
    at: string;
  },
): string {
  const who = [reviewerName, reviewerRole].filter(Boolean).join(' · ') || 'care team';
  const changed = subtrackId && resolvedSubtrackId && subtrackId !== resolvedSubtrackId;
  const subtrackPart = changed
    ? `Subtrack confirmed: ${subtrackId} (was ${resolvedSubtrackId})`
    : `Subtrack confirmed: ${subtrackId ?? '—'}`;
  const elationPart = `Elation record: ${elationVerified ? 'verified' : 'NOT verified'}`;
  const consentPart = `Consent note: ${consentVerified ? 'signed' : 'NOT confirmed'}`;
  return `[First care-team review by ${who} @ ${at}] ${elationPart} · ${consentPart} · ${subtrackPart}`;
}
